import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/client';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ClipboardList, Plus, Trash2, Loader2, Download, GraduationCap } from 'lucide-react';

const ALL = '__all__';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AssignmentsPage() {
  const { get, post, del } = useApi();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('content.assign');
  const [assignments, setAssignments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchFilter, setBatchFilter] = useState(ALL);
  const [loading, setLoading] = useState(true);

  // create
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', instructions: '', batch_id: '', due_date: '', max_points: 100 });
  const [saving, setSaving] = useState(false);

  // grading
  const [grading, setGrading] = useState(null);   // assignment
  const [rows, setRows] = useState([]);
  const [savingRow, setSavingRow] = useState(null);

  useEffect(() => { get('/trainees/batches').then(r => setBatches(r.batches || [])).catch(() => {}); }, [get]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get(`/assignments${batchFilter === ALL ? '' : `?batch_id=${batchFilter}`}`);
      setAssignments(r.assignments || []);
    } catch { toast.error('Failed to load assignments'); }
    finally { setLoading(false); }
  }, [get, batchFilter]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.batch_id) { toast.error('Select a batch'); return; }
    setSaving(true);
    try {
      await post('/assignments', { ...form, max_points: Number(form.max_points) || 100, due_date: form.due_date || null });
      toast.success('Assignment created');
      setOpen(false); setForm({ title: '', instructions: '', batch_id: '', due_date: '', max_points: 100 });
      await load();
    } catch { toast.error('Create failed'); } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}" and its submissions?`)) return;
    try { await del(`/assignments/${a.assignment_id}`); toast.success('Deleted'); await load(); }
    catch { toast.error('Delete failed'); }
  };

  const openGrading = async (a) => {
    setGrading(a);
    try {
      const [subRes, tRes] = await Promise.all([
        get(`/assignments/${a.assignment_id}/submissions`),
        get(`/trainees?batch_id=${a.batch_id}&limit=500`),
      ]);
      const subs = {};
      (subRes.submissions || []).forEach(s => { subs[s.trainee_id] = s; });
      const trainees = tRes.trainees || tRes.items || [];
      setRows(trainees.map(t => ({
        trainee_id: t.trainee_id,
        name: t.name,
        grade: subs[t.trainee_id]?.grade ?? '',
        feedback: subs[t.trainee_id]?.feedback ?? '',
        file_url: subs[t.trainee_id]?.file_url || null,
        file: null,
        submitted: !!subs[t.trainee_id],
      })));
    } catch { toast.error('Failed to load submissions'); }
  };

  const saveRow = async (row) => {
    setSavingRow(row.trainee_id);
    try {
      const fd = new FormData();
      fd.append('trainee_id', row.trainee_id);
      if (row.grade !== '' && row.grade !== null) fd.append('grade', row.grade);
      if (row.feedback) fd.append('feedback', row.feedback);
      if (row.file) fd.append('file', row.file);
      await api.post(`/assignments/${grading.assignment_id}/submissions`, fd);
      toast.success(`Saved ${row.name}`);
      await openGrading(grading);
      await load();
    } catch { toast.error('Save failed'); } finally { setSavingRow(null); }
  };

  const updRow = (tid, patch) => setRows(rs => rs.map(r => r.trainee_id === tid ? { ...r, ...patch } : r));

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="assignments-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center"><ClipboardList className="w-5 h-5 text-amber-600" /></div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Assignments</h1>
            <p className="text-sm text-muted-foreground">Create batch assignments and record trainee submissions & grades.</p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} disabled={!canWrite} data-testid="assignment-new-btn"><Plus className="w-4 h-4 mr-2" />New assignment</Button>
      </div>

      <Select value={batchFilter} onValueChange={setBatchFilter}>
        <SelectTrigger className="w-56" data-testid="assignment-batch-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All batches</SelectItem>
          {batches.map(b => <SelectItem key={b.batch_id} value={b.batch_id}>{b.name}</SelectItem>)}
        </SelectContent>
      </Select>

      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : assignments.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="assignment-empty">No assignments yet.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {assignments.map(a => (
            <Card key={a.assignment_id} className="p-4 space-y-3" data-testid={`assignment-card-${a.assignment_id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="font-display font-semibold truncate">{a.title}</div>
                  <div className="text-xs text-muted-foreground">{a.batch_name || '—'}{a.due_date ? ` · Due ${a.due_date.slice(0, 10)}` : ''}</div>
                </div>
                <Badge variant="secondary">{a.stats?.graded || 0}/{a.stats?.submitted || 0} graded</Badge>
              </div>
              {a.instructions && <p className="text-sm text-muted-foreground line-clamp-2">{a.instructions}</p>}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openGrading(a)} data-testid={`assignment-grade-${a.assignment_id}`}><GraduationCap className="w-3.5 h-3.5 mr-1" />Submissions</Button>
                {canWrite && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(a)} data-testid={`assignment-delete-${a.assignment_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="assignment-create-dialog">
          <DialogHeader><DialogTitle>New assignment</DialogTitle><DialogDescription className="sr-only">Create a batch assignment with title, instructions, and due date.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} data-testid="assignment-title-input" /></div>
            <div className="space-y-1.5"><Label>Instructions (optional)</Label>
              <Textarea value={form.instructions} onChange={(e) => setForm(f => ({ ...f, instructions: e.target.value }))} rows={3} /></div>
            <div className="flex gap-3">
              <div className="space-y-1.5 flex-1"><Label>Batch</Label>
                <Select value={form.batch_id} onValueChange={(v) => setForm(f => ({ ...f, batch_id: v }))}>
                  <SelectTrigger data-testid="assignment-batch-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{batches.map(b => <SelectItem key={b.batch_id} value={b.batch_id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 w-28"><Label>Max points</Label>
                <Input type="number" value={form.max_points} onChange={(e) => setForm(f => ({ ...f, max_points: e.target.value }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>Due date (optional)</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving} data-testid="assignment-create-confirm">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grading dialog */}
      <Dialog open={!!grading} onOpenChange={(o) => !o && setGrading(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="assignment-grading-dialog">
          <DialogHeader><DialogTitle>{grading?.title} — Submissions (max {grading?.max_points})</DialogTitle><DialogDescription className="sr-only">Record each trainee's submission, grade, and feedback.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {rows.length === 0 && <p className="text-sm text-muted-foreground">No trainees in this batch.</p>}
            {rows.map(r => (
              <div key={r.trainee_id} className="rounded-lg border p-3 space-y-2" data-testid={`grade-row-${r.trainee_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">{r.name}{r.submitted && <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-700">Recorded</Badge>}</span>
                  {r.file_url && <a className="text-xs text-amber-600 inline-flex items-center" href={`${BACKEND}${r.file_url}`} target="_blank" rel="noreferrer"><Download className="w-3 h-3 mr-1" />File</a>}
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <Input type="number" className="w-24 h-8" placeholder="Grade" value={r.grade} onChange={(e) => updRow(r.trainee_id, { grade: e.target.value })} data-testid={`grade-input-${r.trainee_id}`} />
                  <Input className="flex-1 min-w-[160px] h-8" placeholder="Feedback" value={r.feedback} onChange={(e) => updRow(r.trainee_id, { feedback: e.target.value })} />
                  <Input type="file" className="w-40 h-8 text-xs" onChange={(e) => updRow(r.trainee_id, { file: e.target.files?.[0] || null })} />
                  <Button size="sm" onClick={() => saveRow(r)} disabled={!canWrite || savingRow === r.trainee_id} data-testid={`grade-save-${r.trainee_id}`}>
                    {savingRow === r.trainee_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
